import React, { useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import apiService from '../services/apiService';
import { useExportExcel } from '../hooks/useExportExcel';

/**
 * Página de gestión de categorías de equipos
 * @returns {JSX.Element}
 */
function Categorias() {
  const { hasPermission } = useAuth();

  // Hook encapsula TODA la lógica CRUD duplicada
  const {
    data: categorias,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem: editingCategoria,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm
  } = usePaginatedEntity({
    entityName: 'categoria',
    entityNamePlural: 'categorias',
    fetchFn: apiService.getCategorias,
    createFn: apiService.createCategoria,
    updateFn: apiService.updateCategoria,
    deleteFn: apiService.deleteCategoria,
    messages: {
      createSuccess: 'Categoria creada',
      updateSuccess: 'Categoria actualizada',
      deleteSuccess: 'Categoria eliminada exitosamente'
    }
  });

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (c) => ({ 'Nombre': c.nombre, 'Descripción': c.descripcion || '', 'Equipos': c.cantidad_equipos || 0 }),
    sheetName: 'Categorías',
    columnWidths: [{ wch: 25 }, { wch: 40 }, { wch: 10 }],
    entityLabel: 'categorías'
  });

  const columns = useMemo(() => [
    { key: 'nombre', label: 'Nombre', sortable: true, highlightable: true, title: true },
    { key: 'descripcion', label: 'Descripcion', highlightable: true },
    {
      key: 'cantidad_equipos',
      label: 'Equipos',
      mobileBadge: true,
      render: (row) => {
        const count = row.cantidad_equipos || 0;
        return <span className={`badge ${count > 0 ? 'badge-success' : 'badge-secondary'}`}>{count} equipo{count !== 1 ? 's' : ''}</span>;
      }
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  const modalFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre', type: 'text', required: true },
    { name: 'descripcion', label: 'Descripcion', type: 'textarea' }
  ], []);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por nombre o descripcion...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Categorías' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nueva Categoría', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('categorias.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={categorias}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron categorias"
          actions={[
            { icon: 'fa-edit',  className: 'btn-ghost-primary', title: 'Editar',   onClick: handleEdit,   show: () => hasPermission('categorias.update') },
            { icon: 'fa-trash', className: 'btn-ghost-danger',  title: 'Eliminar', onClick: handleDelete, show: () => hasPermission('categorias.delete') },
          ]}
          animateRows={true}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingCategoria ? 'Editar Categoria' : 'Nueva Categoria'}
          fields={modalFields}
          initialData={editingCategoria}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Categoria"
          message={`¿Esta seguro de eliminar la categoria "${confirmDialog.item?.nombre}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>
    </>
  );
}

export default Categorias;
